<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 5 — Zoom Course
// Reference: 5. HOME.png
// Layout: warm-gray bg, centered title, 2-col
//   Left (~45%): Redesigned Single General Knowledge Card (bulletless, divided rows, premium)
//   Right (~55%): woman with headphones image + scattered flag/doodle icons

import { computed } from 'vue';
import imgZoom from '~/assets/images/zoom_course.hero.png';

const { t } = useI18n();

const generalItems = computed(() => [
  {
    name: t('zoomCourse.general.items.0'),
    grade: t('zoomCourse.general.grades.0'),
    icon: 'lucide:book-open',
  },
  {
    name: t('zoomCourse.general.items.1'),
    grade: t('zoomCourse.general.grades.1'),
    icon: 'lucide:library',
  },
  {
    name: t('zoomCourse.general.items.2'),
    grade: t('zoomCourse.general.grades.2'),
    icon: 'lucide:graduation-cap',
  },
]);

// Decorative floating icons data
const floatingIcons = [
  { emoji: '🔍', top: '18%', left: '52%', size: '2rem' },
  { emoji: '🇬🇧', top: '24%', left: '62%', size: '2.2rem' },
  { emoji: '🌐', top: '12%', left: '76%', size: '2.2rem' },
  { emoji: '📐', top: '40%', left: '55%', size: '1.8rem' },
  { emoji: '🎬', top: '50%', left: '57%', size: '1.9rem' },
  { emoji: '🇨🇳', top: '65%', left: '52%', size: '2rem' },
  { emoji: '⚛️', top: '64%', left: '62%', size: '2.1rem' },
  { emoji: '🇺🇸', top: '62%', left: '84%', size: '2rem' },
  { emoji: '✏️', top: '10%', left: '88%', size: '1.8rem' },
  { emoji: '📦', top: '14%', left: '94%', size: '1.6rem' },
];
</script>

<template>
  <section id="zoom" class="w-full bg-bg-gray">
    <div class="mx-auto max-w-360 px-6 py-16 md:px-20">
      <!-- Title -->
      <h2 class="mb-12 text-center text-[26px] font-extrabold text-gray-900 sm:text-[32px] md:text-[36px]">
        {{ t('zoomCourse.title') }}
      </h2>

      <!-- 2-col content -->
      <div class="grid grid-cols-1 items-center gap-12 pb-4 md:grid-cols-[45%_55%]">
        <!-- ── Left: Redesigned Single General Knowledge Card ── -->
        <div>
          <div class="rounded-[24px] border border-gray-100/50 bg-white p-5 shadow-sm sm:p-8 md:p-10">
            <!-- Header with a decorative brand color indicator -->
            <div class="flex items-center gap-3">
              <span class="h-6 w-1.5 rounded-full bg-brand-blue"></span>
              <h3 class="text-[18px] font-extrabold text-gray-900 sm:text-[20px] md:text-[22px]">
                {{ t('zoomCourse.general.title') }}
              </h3>
            </div>

            <!-- Description -->
            <p class="mt-3.5 text-[13.5px] leading-relaxed text-gray-500 sm:text-[14.5px]">
              {{ t('zoomCourse.general.description') }}
            </p>

            <!-- 3 Rows of design -->
            <div class="mt-6 space-y-3 sm:mt-8 sm:space-y-4">
              <div
                v-for="(item, idx) in generalItems"
                :key="idx"
                class="group flex cursor-pointer items-center justify-between rounded-xl border border-gray-100/70 bg-gray-50/30 p-3.5 transition-all duration-300 hover:border-brand-blue/20 hover:bg-brand-blue/5 sm:p-4"
              >
                <div class="flex items-center gap-3 sm:gap-4">
                  <!-- Row Icon with background that changes color on hover -->
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 group-hover:border-brand-blue group-hover:bg-brand-blue sm:h-11 sm:w-11"
                  >
                    <Icon
                      :name="item.icon"
                      class="h-5 w-5 text-brand-blue transition-colors duration-300 group-hover:text-white sm:h-5.5 sm:w-5.5"
                    />
                  </div>
                  <!-- Category Name -->
                  <span
                    class="text-[14px] font-bold text-gray-800 transition-colors duration-300 group-hover:text-brand-blue sm:text-[15.5px]"
                  >
                    {{ item.name }}
                  </span>
                </div>
                <!-- Grade range badge -->
                <span
                  class="rounded-full bg-brand-blue/8 px-2.5 py-1 text-[11.5px] font-bold whitespace-nowrap text-brand-blue transition-all duration-300 group-hover:bg-brand-blue/15 sm:px-3 sm:py-1.5 sm:text-[12.5px]"
                >
                  {{ item.grade }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Right: Woman image + scattered floating icons ── -->
        <div class="relative flex h-70 items-end justify-end overflow-visible sm:h-95 md:h-120">
          <!-- Floating emoji/icon decorations -->
          <span
            v-for="(icon, i) in floatingIcons"
            :key="i"
            class="pointer-events-none absolute select-none"
            :style="{
              top: icon.top,
              left: icon.left,
              fontSize: icon.size,
              opacity: 0.85,
            }"
          >
            {{ icon.emoji }}
          </span>

          <!-- Zoom course hero image (woman with headphones at laptop) -->
          <div
            class="relative z-10 h-full w-120"
            style="
              -webkit-mask-image:
                linear-gradient(to bottom, black 90%, transparent 100%),
                linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
              mask-image:
                linear-gradient(to bottom, black 90%, transparent 100%),
                linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
              -webkit-mask-composite: source-in;
              mask-composite: intersect;
            "
          >
            <AppImage
              :src="imgZoom"
              alt="Zoom Course"
              img-class="h-full w-full object-contain object-bottom"
              loader-size="h-12 w-12"
              loader-overlay-class="bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* intentionally empty — mask is applied inline on the image wrapper */
</style>
